import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Explainer,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useCreateContact, orgErrorMessage } from '@/api/organization';
import { useMyTeams, useInviteMembers } from '@/api/teams';

interface InviteMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NO_TEAM = '__none__';

/**
 * Org-level "Invite member" — creates a contact with a login provisioned (a
 * branded set-password link is emailed) and optionally seeds a team invite in
 * the same step. Org role (Owner/Admin) is assigned later from the person
 * drawer once the member has accepted and has a login id.
 */
export function InviteMemberDialog({ open, onOpenChange }: InviteMemberDialogProps) {
  const { show } = useToast();
  const { data: teams = [] } = useMyTeams();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [teamId, setTeamId] = useState<string>(NO_TEAM);
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});

  useEffect(() => {
    if (open) {
      setName('');
      setEmail('');
      setTeamId(NO_TEAM);
      setErrors({});
    }
  }, [open]);

  const inviteToTeam = useInviteMembers(teamId === NO_TEAM ? '' : teamId);

  const create = useCreateContact({
    onSuccess: () => {
      if (teamId !== NO_TEAM) {
        inviteToTeam.mutate(
          // Org-level invites are restricted by default: the new member sees only
          // resources explicitly shared with them, not the whole organization.
          { emails: [email.trim()], restricted: true },
          {
            onSettled: () => {
              show({ tone: 'success', title: 'Member invited', description: 'A set-password link was emailed.' });
              onOpenChange(false);
            },
          },
        );
      } else {
        show({ tone: 'success', title: 'Member invited', description: 'A set-password link was emailed.' });
        onOpenChange(false);
      }
    },
    onError: (err) => show({ tone: 'danger', title: orgErrorMessage(err, 'Could not invite member') }),
  });

  const pending = create.isPending || inviteToTeam.isPending;

  function handleSubmit() {
    const next: typeof errors = {};
    if (!name.trim()) next.name = 'Enter a name.';
    if (!email.trim()) next.email = 'Enter an email.';
    else if (!EMAIL_RE.test(email.trim())) next.email = 'Enter a valid email.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    create.mutate({
      contact_name: name.trim(),
      contact_email: email.trim(),
      provision_login: true,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Invite member</DialogTitle>
          <DialogDescription>
            Create an account for someone in your organization. They can sign in once they set a password.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <form
            id="invite-member-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            style={{ display: 'grid', gap: 'var(--vn-space-4)' }}
          >
            <Field label="Name" required error={errors.name}>
              {(props) => (
                <Input {...props} value={name} invalid={!!errors.name} onChange={(e) => setName(e.target.value)} />
              )}
            </Field>

            <Field label="Email" required error={errors.email}>
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  value={email}
                  invalid={!!errors.email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}
            </Field>

            {teams.length > 0 && (
              <Field label="Add to a team" description="Optional — sends them a team invite too.">
                {() => (
                  <Select value={teamId} onValueChange={setTeamId}>
                    <SelectTrigger>
                      <SelectValue placeholder="No team" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_TEAM}>No team</SelectItem>
                      {teams.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.team_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            )}

            <Explainer title="How the invite works">
              We email a branded set-password link. The person sets their own password — no default password is
              sent. They appear as a member once invited, and you can set their organization role after they sign
              in.
            </Explainer>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="invite-member-form" disabled={pending}>
            {pending ? 'Inviting…' : 'Invite member'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
